import React, { useCallback, useEffect, useState } from 'react';

import { LoginCredentials } from '@opendoor/auth-fe';
import { Box, Button, Flex, Input, Loader, Text } from '@opendoor/bricks/core';
import { GoogleAuth } from '@opendoor/cinderblocks/platform/auth/GoogleAuth';
import { Form } from '@opendoor/cinderblocks/platform/auth/shared';

import { useAuthAuthenticated } from 'platform/auth-fe';
import { useGetAmplitudeFeatureFlag } from 'platform/graphql/useAmplitude';
import { LegacyExternalLink } from 'platform/remix-run/react';

import { coreExpNovoAuth } from 'shared/declarations/AmplitudeFeatureFlags';
import { GOOGLE_CLIENT_ID } from 'shared/helpers/globals';
import { useLazyAsyncFn } from 'shared/helpers/hooks/useLazyAsyncFn';

export interface ReauthFormProps {
  onSuccess: () => void;
}

/** UI for the user to reauthenticate. Commonly used before allowing the user to view sensitive data. */
export const ReauthForm: React.FC<ReauthFormProps> = ({ onSuccess }) => {
  const { user, verifyLogin: _verifyLogin } = useAuthAuthenticated();
  const [_verifyLoginLazy, verifyLoginResult] = useLazyAsyncFn(_verifyLogin);
  const verifyLogin = useCallback(
    async (credentials: LoginCredentials) => {
      const result = await _verifyLoginLazy(credentials);
      if (result.success) {
        onSuccess();
      } // errors handled with loginLastCall
    },
    [_verifyLoginLazy, onSuccess],
  );

  const [password, setPassword] = useState('');
  const [showNovoStyle, setShowNovoStyle] = useState(true);

  const { variant, loading } = useGetAmplitudeFeatureFlag(coreExpNovoAuth, 'off');
  useEffect(() => {
    if (!loading) {
      setShowNovoStyle(variant === 'on');
    }
  }, [variant, loading]);

  return loading ? (
    <Loader />
  ) : showNovoStyle ? (
    <Flex flexDirection="column">
      <Text textAlign="center">Signing in as {user.email}</Text>
      <Form
        onSubmit={(event) => {
          event.preventDefault();
          return verifyLogin({ email: user.email, password });
        }}
      >
        <Box mt={5} width="100%">
          <Input
            analyticsName="cake-reauthform-password"
            id="reauth-password"
            type="password"
            label="Password"
            placeholder="Password"
            unboundedWidth
            onChange={(_event, value) => {
              setPassword(value || '');
            }}
          />
        </Box>
        <Box mt={7} width="100%">
          <Button
            type="submit"
            analyticsName="reauth-submit"
            aria-label="Confirm and submit password"
            loading={verifyLoginResult?.loading}
            loadingText="Confirming"
            disabled={verifyLoginResult?.loading || password.length === 0}
          >
            Confirm
          </Button>
          {verifyLoginResult.data && !verifyLoginResult.data.success && (
            <Box mt={5}>
              <Text color="red50">{verifyLoginResult.data.displayErrorMessage}</Text>
              <LegacyExternalLink
                analyticsName="cake-reauth-reset-password-link"
                aria-label="Opens reset password page in a new tab"
                href="https://www.opendoor.com/reset-password/new"
                target="_blank"
                color="red50"
                textDecoration="underline"
                _hover={{
                  color: 'red50',
                }}
              >
                Click here to reset your password.
              </LegacyExternalLink>
            </Box>
          )}
        </Box>
      </Form>
      <Box mt={7} width="100%">
        <GoogleAuth
          clientId={GOOGLE_CLIENT_ID}
          onAuthed={(response) => verifyLogin({ googleIdToken: response.tokenId })}
          showNovoStyle={true}
        />
      </Box>
    </Flex>
  ) : (
    <Flex flexDirection="column">
      <Text textAlign="center">Signing in as {user.email}</Text>
      <Form
        onSubmit={(event) => {
          event.preventDefault();
          return verifyLogin({ email: user.email, password });
        }}
      >
        <Box mt={5} width="100%">
          <Input
            analyticsName="cake-reauthform-password"
            id="reauth-password"
            type="password"
            label="Password"
            placeholder="Password"
            unboundedWidth
            onChange={(_event, value) => {
              setPassword(value || '');
            }}
          />
        </Box>
        <Box mt={7} width="100%">
          <Button
            type="submit"
            analyticsName="reauth-submit"
            aria-label="Confirm and submit password"
            loading={verifyLoginResult?.loading}
            loadingText="Confirming"
            disabled={verifyLoginResult?.loading || password.length === 0}
          >
            Confirm
          </Button>
          {verifyLoginResult.data && !verifyLoginResult.data.success && (
            <Box mt={5}>
              <Text color="red50">{verifyLoginResult.data.displayErrorMessage}</Text>
              <LegacyExternalLink
                analyticsName="cake-reauth-reset-password-link"
                aria-label="Opens reset password page in a new tab"
                href="https://www.opendoor.com/reset-password/new"
                target="_blank"
                color="red50"
                textDecoration="underline"
                _hover={{
                  color: 'red50',
                }}
              >
                Click here to reset your password.
              </LegacyExternalLink>
            </Box>
          )}
        </Box>
      </Form>
      <Box mt={7} width="100%">
        <GoogleAuth
          clientId={GOOGLE_CLIENT_ID}
          onAuthed={(response) => verifyLogin({ googleIdToken: response.tokenId })}
          showNovoStyle={false}
        />
      </Box>
    </Flex>
  );
};
